import { useQuery } from '@tanstack/react-query';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@nesso/ui/primitives/card';

import { formatPlanTerms, paidPlansOptions, subscriptionMeOptions } from '@/entities/subscription';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatShortDate } from '@/shared/lib/time-utils';

type UpdatePlanSummaryProps = {
    planCode: string;
};

export function UpdatePlanSummary({ planCode }: UpdatePlanSummaryProps) {
    const { data: plans } = useQuery(paidPlansOptions());
    const { data: subscription } = useQuery(subscriptionMeOptions());

    const targetPlan = plans?.find((plan) => plan.code === planCode);
    const planTerms = targetPlan ? formatPlanTerms(targetPlan) : null;
    const isRevertToCurrent = subscription != null && planCode === subscription.planCode;
    const effectiveDate = subscription?.currentPeriodEnd
        ? new Date(subscription.currentPeriodEnd)
        : undefined;

    return (
        <div className="flex flex-col gap-10">
            <div>
                <p className="text-lg font-semibold">Updated plan</p>
                {isRevertToCurrent ? (
                    <p className="text-base">Plan will take effect immediately</p>
                ) : (
                    effectiveDate && (
                        <p className="text-base">
                            Plan will take effect {formatShortDate(effectiveDate)}
                        </p>
                    )
                )}
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>{targetPlan?.name ?? 'Selected plan'}</CardTitle>
                    {planTerms && <CardDescription>{planTerms}</CardDescription>}
                </CardHeader>
                <CardContent className="flex items-center gap-3 pt-4">
                    <span className="text-3xl font-semibold">
                        {targetPlan ? formatCurrencyFromCents(targetPlan.basePriceCents) : '—'}
                    </span>
                    <span className="text-sm leading-none text-muted-foreground">
                        per
                        <br />
                        month
                    </span>
                </CardContent>
            </Card>
        </div>
    );
}
